/*
 * Dates in the UI. Activity timestamps show in the viewer's time zone. A
 * certificate's issue date is fixed in the zone it was issued in (see
 * formatCertificateDate in @ready/shared), so it matches the PDF everywhere.
 */
import { zoneAbbreviation } from '@ready/shared';

export { formatCertificateDate } from '@ready/shared';

/** The browser's IANA time zone, e.g. "Asia/Kolkata". */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** "October 12, 2026" in the viewer's zone. */
export function formatDate(at: string | Date): string {
  return new Date(at).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Oct 12, 2026, 9:14 PM" in the viewer's zone. */
export function formatDateTime(at: string | Date): string {
  return new Date(at).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "9:14 PM" in the viewer's zone, with the zone's short name ("9:14 PM EDT", "6:00 PM IST"). */
export function formatTimeWithZone(at: string | Date): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${time} ${zoneAbbreviation(d, browserTimeZone())}`;
}

/**
 * An event session's time in a given zone: "Tue, Nov 4, 2026 · 6:00–7:30 PM IST".
 * Pass the viewer's zone (default) or the event's own.
 */
export function formatSessionTime(
  startsAt: string | Date,
  endsAt: string | Date,
  timeZone = browserTimeZone(),
): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  });
  const t = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });
  return `${day} · ${t(start)}–${t(end)} ${zoneAbbreviation(start, timeZone)}`;
}

/**
 * The wall-clock parts of an instant in a zone, as an `<input type="datetime-local">`
 * value ("2026-11-04T18:00"), for editing a session in the event's own zone.
 */
export function toZonedInput(at: string | Date, timeZone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(at))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * The instant a wall-clock time ("2026-11-04T18:00") means in a zone, as an ISO
 * string. Works for any zone regardless of the browser's own: guess the
 * instant as if it were UTC, see what that shows in the zone, and correct by
 * the difference (twice, for daylight-saving edges).
 */
export function fromZonedInput(local: string, timeZone: string): string {
  const [date, time] = local.split('T');
  const [y, mo, d] = date!.split('-').map(Number);
  const [h, mi] = time!.split(':').map(Number);
  const wanted = Date.UTC(y!, mo! - 1, d!, h!, mi!);
  let guess = wanted;
  for (let i = 0; i < 2; i++) {
    const shown = toZonedInput(new Date(guess), timeZone);
    const [sd, st] = shown.split('T');
    const [sy, smo, sdd] = sd!.split('-').map(Number);
    const [sh, smi] = st!.split(':').map(Number);
    guess += wanted - Date.UTC(sy!, smo! - 1, sdd!, sh!, smi!);
  }
  return new Date(guess).toISOString();
}

/** Every IANA zone the browser knows, for the Account setting. */
export function allTimeZones(): string[] {
  return typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
}
